import type { AgentDefinitionResponse } from '@erp/rpc';

/**
 * Pure form state for the agent-definition editor (AI-458).
 *
 * Seeding the form from a version and deciding what counts as dirty are the two rules that gate
 * saving and the leave-the-tab warning, so they live here — outside the provider — where they can
 * be unit-tested without mounting React.
 */

export type AgentFormFields = {
  name: string;
  description: string;
  systemPrompt: string;
  modelConfig: AgentDefinitionResponse['modelConfig'];
  config: AgentDefinitionResponse['config'];
  inputFields: AgentDefinitionResponse['inputFields'];
  outputFields: AgentDefinitionResponse['outputFields'];
};

/**
 * The behavior payload the form edits — carried by a version detail, or by the definition itself
 * before/outside version selection.
 */
export type AgentFormBehaviorSource = Pick<
  AgentDefinitionResponse,
  'systemPrompt' | 'modelConfig' | 'config' | 'inputFields' | 'outputFields'
>;

/**
 * Everything `initAgentForm` reads. Deliberately narrower than `AgentDefinitionResponse` so callers
 * and tests can supply exactly these fields instead of asserting a whole response shape.
 */
export type AgentFormSource = Pick<
  AgentDefinitionResponse,
  'name' | 'description'
> &
  AgentFormBehaviorSource;

/**
 * Render a definition plus the version being viewed as editable form fields.
 *
 * Name and description come from the definition (they are not versioned); everything else comes
 * from `behavior`, which is why viewing an old version shows that version's prompt and config.
 */
export function initAgentForm(
  definition: AgentFormSource,
  behavior: AgentFormBehaviorSource = definition,
): AgentFormFields {
  return {
    name: definition.name,
    description: definition.description ?? '',
    systemPrompt: behavior.systemPrompt,
    modelConfig: { ...behavior.modelConfig },
    config: { ...behavior.config },
    inputFields: [...behavior.inputFields],
    outputFields: [...behavior.outputFields],
  };
}

/**
 * Config keys owned by the Agent tab through `agentForm.config` rather than by a tab-local
 * override. See `mergeConfigForSave`.
 */
const FORM_OWNED_CONFIG_KEYS = ['taskManagement', 'failureActions'] as const;

/**
 * The config to persist: the form's config with the tab-local overrides applied on top.
 *
 * `localConfig` is a whole-config snapshot, not a sparse patch — the Tools and Procedures tabs seed
 * it from the selected version's config — so it carries a stale copy of keys those tabs do not own.
 * `taskManagement` and `failureActions` are written by the Agent tab into `agentForm.config`, so
 * they are re-asserted after the overrides. Without that, editing either and then toggling a tool
 * in the same session discards the Agent-tab edit silently.
 */
export function mergeConfigForSave(
  formConfig: AgentDefinitionResponse['config'],
  localConfig: AgentDefinitionResponse['config'] | null,
): AgentDefinitionResponse['config'] {
  if (!localConfig) return { ...formConfig };

  const merged: AgentDefinitionResponse['config'] = {
    ...formConfig,
    ...localConfig,
  };
  for (const key of FORM_OWNED_CONFIG_KEYS) {
    if (key in formConfig) {
      merged[key] = formConfig[key];
    } else {
      delete merged[key];
    }
  }
  return merged;
}

export interface AgentDefinitionDirtyState {
  /** Name/description — saved against the definition, not the draft version. */
  isMetadataDirty: boolean;
  /** Prompt/model/config/fields — saved against the draft version. */
  isPayloadDirty: boolean;
  /** Either of the two above; what the Save affordance keys off. */
  isAgentDirty: boolean;
  isToolsDirty: boolean;
  isConfigDirty: boolean;
  isDirty: boolean;
}

/** The versioned payload fields, compared as a unit. */
function payloadOf(form: AgentFormFields) {
  return JSON.stringify({
    systemPrompt: form.systemPrompt,
    modelConfig: form.modelConfig,
    config: form.config,
    inputFields: form.inputFields,
    outputFields: form.outputFields,
  });
}

/** Tool selection is a set, so reordering must not register as a change. */
function toolSetOf(tools: string[]) {
  return JSON.stringify([...tools].sort());
}

/**
 * Which parts of the edit differ from the version being viewed.
 *
 * `localTools`/`localConfig` are null until the user touches those editors; an untouched editor is
 * never dirty even if the derived list would compare unequal.
 */
export function deriveDirtyState({
  baselineForm,
  agentForm,
  localTools,
  enabledTools,
  originalTools,
  localConfig,
  baseConfig,
}: {
  /** The viewed version as form fields, or null before the form is seeded. */
  baselineForm: AgentFormFields | null;
  agentForm: AgentFormFields | null;
  localTools: string[] | null;
  enabledTools: string[];
  originalTools: string[];
  localConfig: AgentDefinitionResponse['config'] | null;
  baseConfig: AgentDefinitionResponse['config'];
}): AgentDefinitionDirtyState {
  const comparable = baselineForm !== null && agentForm !== null;
  const isMetadataDirty =
    comparable &&
    (baselineForm.name !== agentForm.name ||
      baselineForm.description !== agentForm.description);
  const isPayloadDirty =
    comparable && payloadOf(baselineForm) !== payloadOf(agentForm);
  const isAgentDirty = isMetadataDirty || isPayloadDirty;

  const isToolsDirty =
    localTools !== null && toolSetOf(enabledTools) !== toolSetOf(originalTools);

  const isConfigDirty =
    localConfig !== null &&
    JSON.stringify(localConfig) !== JSON.stringify(baseConfig);

  return {
    isMetadataDirty,
    isPayloadDirty,
    isAgentDirty,
    isToolsDirty,
    isConfigDirty,
    isDirty: isAgentDirty || isToolsDirty || isConfigDirty,
  };
}
